import { useState, useCallback } from 'react';
import { formatPrice } from '../../utils/formatPrice';

// ---------------------------------------------------------------------------
// Cart line item
// ---------------------------------------------------------------------------

function CartItem({ item, onUpdateQuantity, onRemove, onSetLineDiscount }) {
  const [discountOpen, setDiscountOpen] = useState(false);
  const { product, quantity, discountType, discountValue } = item;

  const price = parseFloat(product.price) || 0;
  const lineSubtotal = price * quantity;

  let lineDiscount = 0;
  if (discountType === 'percent' && discountValue > 0) {
    lineDiscount = lineSubtotal * (Math.min(discountValue, 100) / 100);
  } else if (discountType === 'fixed' && discountValue > 0) {
    lineDiscount = Math.min(discountValue * quantity, lineSubtotal);
  }

  const lineTotal = lineSubtotal - lineDiscount;

  const handleQuantityChange = useCallback(
    (e) => {
      const val = parseInt(e.target.value, 10);
      if (!isNaN(val) && val >= 1) {
        onUpdateQuantity(product, val);
      }
    },
    [product, onUpdateQuantity]
  );

  const handleDiscountTypeChange = useCallback(
    (e) => {
      const newType = e.target.value;
      onSetLineDiscount(product, newType, newType ? discountValue : 0);
    },
    [product, discountValue, onSetLineDiscount]
  );

  const handleDiscountValueChange = useCallback(
    (e) => {
      const val = e.target.value;
      onSetLineDiscount(product, discountType, val);
    },
    [product, discountType, onSetLineDiscount]
  );

  return (
    <div className="symple-pos-cart-item border-b border-gray-200 py-3 px-4">
      {/* Top row: name, sku, remove */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {product.name}
          </p>
          {product.sku && (
            <p className="text-xs text-gray-400 mt-0.5">
              SKU: {product.sku}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-0.5">
            {formatPrice(price)} each
          </p>
        </div>
        <button
          type="button"
          onClick={() => onRemove(product)}
          className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors p-1 -mr-1 -mt-1"
          aria-label={`Remove ${product.name} from cart`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {/* Quantity controls + line total */}
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onUpdateQuantity(product, quantity - 1)}
            disabled={quantity <= 1}
            className="w-7 h-7 flex items-center justify-center rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            aria-label="Decrease quantity"
          >
            -
          </button>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={handleQuantityChange}
            className="w-12 h-7 text-center text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-pos-primary focus:border-pos-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            aria-label="Quantity"
          />
          <button
            type="button"
            onClick={() => onUpdateQuantity(product, quantity + 1)}
            className="w-7 h-7 flex items-center justify-center rounded bg-gray-100 text-gray-600 hover:bg-gray-200 text-sm font-medium transition-colors"
            aria-label="Increase quantity"
          >
            +
          </button>
        </div>

        <p className="text-sm font-semibold text-gray-900">
          {formatPrice(lineTotal)}
        </p>
      </div>

      {/* Discount toggle */}
      <div className="mt-2">
        <button
          type="button"
          onClick={() => setDiscountOpen((prev) => !prev)}
          className="text-xs text-pos-primary hover:text-pos-primary-dark transition-colors flex items-center gap-1"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-3 w-3 transform transition-transform ${discountOpen ? 'rotate-90' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clipRule="evenodd"
            />
          </svg>
          {lineDiscount > 0
            ? `Discount: -${formatPrice(lineDiscount)}`
            : 'Add discount'}
        </button>

        {discountOpen && (
          <div className="flex items-center gap-2 mt-2">
            <select
              value={discountType}
              onChange={handleDiscountTypeChange}
              className="h-7 text-xs border border-gray-300 rounded px-1 focus:outline-none focus:ring-1 focus:ring-pos-primary focus:border-pos-primary bg-white"
              aria-label="Discount type"
            >
              <option value="">None</option>
              <option value="percent">%</option>
              <option value="fixed">Fixed</option>
            </select>
            {discountType && (
              <input
                type="number"
                min="0"
                step="any"
                value={discountValue || ''}
                onChange={handleDiscountValueChange}
                placeholder={discountType === 'percent' ? '0%' : '0.00'}
                className="w-20 h-7 text-xs border border-gray-300 rounded px-2 focus:outline-none focus:ring-1 focus:ring-pos-primary focus:border-pos-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                aria-label="Discount value"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyCart() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 px-6">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-16 w-16 mb-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
        />
      </svg>
      <p className="text-lg font-medium">Cart is empty</p>
      <p className="text-sm mt-1">Add products to get started</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Cart component
// ---------------------------------------------------------------------------

export default function Cart({ cart, onCheckout, taxRate }) {
  const {
    items,
    addItem,
    removeItem,
    updateQuantity,
    setLineDiscount,
    clearCart,
    subtotal,
    discountTotal,
    taxTotal,
    total,
    itemCount,
  } = cart;

  const handleCheckout = useCallback(() => {
    if (typeof onCheckout === 'function') {
      onCheckout({
        items,
        subtotal,
        discountTotal,
        taxTotal,
        total,
      });
    }
  }, [onCheckout, items, subtotal, discountTotal, taxTotal, total]);

  const hasDiscount = discountTotal > 0;
  const hasTax = taxTotal > 0;

  return (
    <div className="symple-pos-wrap">
      <aside className="w-96 h-full flex flex-col bg-white border-l border-pos-border">
        {/* ---- Header ---- */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-pos-border bg-pos-muted flex-shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-pos-foreground">Cart</h2>
            {itemCount > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-pos-navy rounded-full">
                {itemCount}
              </span>
            )}
          </div>
          {items.length > 0 && (
            <button
              type="button"
              onClick={clearCart}
              className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
            >
              Clear cart
            </button>
          )}
        </div>

        {/* ---- Items list ---- */}
        {items.length === 0 ? (
          <EmptyCart />
        ) : (
          <div className="flex-1 overflow-y-auto">
            {items.map((item) => {
              const key = item.product.variation_id
                ? item.product.variation_id
                : item.product.id;
              return (
                <CartItem
                  key={key}
                  item={item}
                  onUpdateQuantity={updateQuantity}
                  onRemove={removeItem}
                  onSetLineDiscount={setLineDiscount}
                />
              );
            })}
          </div>
        )}

        {/* ---- Footer / Totals ---- */}
        {items.length > 0 && (
          <div className="flex-shrink-0 border-t border-pos-border bg-pos-muted px-4 py-4">
            {/* Subtotal */}
            <div className="flex justify-between text-sm text-gray-600">
              <span>Subtotal</span>
              <span>{formatPrice(subtotal)}</span>
            </div>

            {/* Discount */}
            {hasDiscount && (
              <div className="flex justify-between text-sm text-red-500 mt-1">
                <span>Discount</span>
                <span>-{formatPrice(discountTotal)}</span>
              </div>
            )}

            {/* Tax */}
            {hasTax && (
              <div className="flex justify-between text-sm text-gray-600 mt-1">
                <span>Tax</span>
                <span>{formatPrice(taxTotal)}</span>
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-gray-300 my-3" />

            {/* Grand total */}
            <div className="flex justify-between items-center">
              <span className="text-base font-bold text-gray-900">Total</span>
              <span className="text-xl font-bold text-gray-900">
                {formatPrice(total)}
              </span>
            </div>

            {/* Charge button */}
            <button
              type="button"
              onClick={handleCheckout}
              className="mt-4 w-full py-3 bg-pos-primary hover:bg-pos-primary-dark active:bg-pos-primary-dark text-white text-base font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-pos-primary/50 focus:ring-offset-2"
            >
              Checkout {formatPrice(total)}
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
